'use client';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GhgPremiumConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function GhgPremiumConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
}: GhgPremiumConfirmationDialogProps) {
  const { t } = useTranslation();
  const [allowDataGathering, setAllowDataGathering] = useState(false);
  const [confirmAgreement, setConfirmAgreement] = useState(false);

  const canConfirm = allowDataGathering && confirmAgreement;

  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm();
      setAllowDataGathering(false);
      setConfirmAgreement(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setAllowDataGathering(false);
    setConfirmAgreement(false);
  };

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (canConfirm) {
      onOpenChange(false);
      setAllowDataGathering(false);
      setConfirmAgreement(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!canConfirm}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {t('deals.new.financesConfiguration.calculationDrawer.ghgPremium.ghgPremiumDialogTitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowDataGathering}
                onChange={(e) => setAllowDataGathering(e.target.checked)}
                color="primary"
                sx={{ alignSelf: 'flex-start', mt: -1 }}
              />
            }
            label={t(
              'deals.new.financesConfiguration.calculationDrawer.ghgPremium.ghgPremiumDialogAuthorizeCheckbox'
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmAgreement}
                onChange={(e) => setConfirmAgreement(e.target.checked)}
                color="primary"
                sx={{ alignSelf: 'flex-start', mt: -1 }}
              />
            }
            label={t(
              'deals.new.financesConfiguration.calculationDrawer.ghgPremium.ghgPremiumDialogConfirmationCheckbox'
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          {t('deals.new.financesConfiguration.calculationDrawer.ghgPremium.cancelButtonText')}
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={!canConfirm}>
          {t('deals.new.financesConfiguration.calculationDrawer.ghgPremium.okButtonText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
